import {
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
  CognitoUserAttribute,
} from "amazon-cognito-identity-js";
import { jwtDecode } from "jwt-decode";
import { AFTER_SIGN_OUT_PATH } from "../../constants/urls";
import { cleanUserArtifacts } from "../../utils/user";
import { retrieveAuthToken } from "../../utils/authService";
import { cognitoPoolData } from "./cognitoPoolData";
import {
  CognitoSignUpParams,
  CognitoResendConfirmationCodeParams,
} from "./types/CognitoSignUpParams";

const AmazonCognitoIdentity = import("amazon-cognito-identity-js");

/**
 * Signs in a user to Cognito using the provided tokens. Follows this up by retrieving the authToken.
 *
 * @param {Object} tokens - The tokens required for Cognito sign-in.
 * @param {string} tokens.idToken - The ID token for the user.
 * @param {string} tokens.accessToken - The access token for the user.
 * @param {string} tokens.refreshToken - The refresh token for the user.
 * @returns {Promise<void>} A promise that resolves when the sign-in process is complete.
 */
export const doCognitoSignIn = async (tokens: {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}) => {
  const email = getEmailFromJWT(tokens.idToken);

  if (!email) {
    throw new Error("No email found in Cognito ID token");
  }

  const cognitoUserSession = new CognitoUserSession({
    IdToken: new CognitoIdToken({ IdToken: tokens.idToken }),
    AccessToken: new CognitoAccessToken({ AccessToken: tokens.accessToken }),
    RefreshToken: new CognitoRefreshToken({
      RefreshToken: tokens.refreshToken,
    }),
  });

  getCognitoUser({ email }).setSignInUserSession(cognitoUserSession);

  await retrieveAuthToken();
};

export const getCognitoUserPool = () => {
  return new CognitoUserPool(cognitoPoolData());
};

export const getCognitoJwt = async ({ forceRefresh = false, noRedirect = false }) => {
  let token = null;
  const user = getCognitoCurrentUser();
  if (!user) {
    // No current user, so no Jwt to return
    return null;
  }
  try {
    const session = await getUserSession(user);
    token = session.getIdToken();

    // If forceRefresh OR the current token is expired, try to get a new one
    const now = new Date();
    if (forceRefresh || now.getTime() > token.getExpiration() * 1000) {
      token = await refreshCognitoUserSession(user, session.getRefreshToken());
    }
  } catch (e) {
    if (e instanceof Error && e.name === "NotAuthorizedException") {
      console.log("Caught NotAuthorizedException while getting user session - logging out");
      if (!noRedirect) {
        cognitoSignOut();
        cleanUserArtifacts();
        window.location.href = AFTER_SIGN_OUT_PATH;
      }
    }
    return null;
  }

  return token.getJwtToken();
};

export const getCognitoCurrentUser = () => {
  return getCognitoUserPool().getCurrentUser();
};

export const cognitoSignOut = () => {
  const user = getCognitoCurrentUser();
  if (user) {
    user.signOut();
  }
};

export const cognitoGlobalSignOut = async () => {
  let user;
  try {
    user = getCognitoCurrentUser();
    if (!user) {
      return null;
    }

    // globalSignOut only works if we get the user session first
    await getUserSession(user);
    await doGlobalSignOut(user);
  } catch (err) {
    let message = "Error signing out user";
    if (err instanceof Error) {
      message = err.message;
    }
    console.error(`error performing globalSignOut. falling back to local sign out: ${message}`);
    if (user) {
      user.signOut();
    }
  }
};

const doGlobalSignOut = (cognitoUser: CognitoUser) => {
  return new Promise((resolve, reject) => {
    cognitoUser.globalSignOut({
      onSuccess: msg => {
        resolve(msg);
      },
      onFailure: err => {
        reject(err);
      },
    });
  });
};

const getUserSession = (user: CognitoUser): Promise<CognitoUserSession> => {
  return new Promise((resolve, reject) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err) return reject(err);
      if (!session) {
        return reject(new Error("No session found"));
      }

      resolve(session);
    });
  });
};

const refreshCognitoUserSession = async (
  cognitoUser: CognitoUser,
  refresh_token: CognitoRefreshToken
): Promise<CognitoIdToken> => {
  return new Promise(resolve => {
    cognitoUser.refreshSession(refresh_token, (err, session) => {
      if (err) {
        console.log(err);
        cognitoSignOut();
        cleanUserArtifacts();
        window.location.href = AFTER_SIGN_OUT_PATH;
      } else {
        const idToken = session.getIdToken();
        resolve(idToken);
      }
    });
  });
};

export const getCognitoUser = ({ email }: { email: string }) => {
  const userPool = getCognitoUserPool();

  const userData = {
    Username: decodeURIComponent(email.toLowerCase()),
    Pool: userPool,
  };

  return new CognitoUser(userData);
};

export const asyncPasswordAuth = async ({
  email,
  password,
}: {
  email: string;
  password: string;
}) => {
  const userData = {
    Username: email.toLowerCase(),
    Password: password,
  };

  const authenticationDetails = new (await AmazonCognitoIdentity).AuthenticationDetails(userData);
  const cognitoUser = getCognitoUser({ email });
  return new Promise<void>((resolve, reject) => {
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: async () => {
        await retrieveAuthToken();
        resolve();
      },
      onFailure: err => reject(err.message),
    });
  });
};

const getEmailFromJWT = (jwt: string): string | null => {
  const claims = jwtDecode(jwt);

  if ("email" in claims && typeof claims.email === "string") {
    return claims.email;
  } else if (
    "cognito:username" in claims &&
    typeof claims["cognito:username"] === "string" &&
    claims["cognito:username"].includes("@") &&
    claims["cognito:username"].includes(".")
  ) {
    const username = claims["cognito:username"];

    const i = username.indexOf("_");
    const emailParts = [username.slice(0, i), username.slice(i + 1)];

    if (emailParts.length > 1) {
      return emailParts[1];
    }
    return emailParts[0];
  }

  return null;
};

/**
 * Signs up a user to Cognito.
 *
 * @param {CognitoSignUpParams} params - The parameters for signing up a user.
 * @param {string} params.email - The email address of the user.
 * @param {string} params.password - The password for the user.
 * @param {Record<string, string>} [params.metadata] - Optional metadata to include in the sign-up request.
 * @returns {Promise<void>} A promise that resolves when the sign-up is successful or rejects with an error message.
 */
export const cognitoSignUp = ({
  email,
  password,
  attributes = {},
  metadata = {},
}: CognitoSignUpParams): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      const userPool = getCognitoUserPool();

      const userAttributes = [
        new CognitoUserAttribute({
          Name: "email",
          Value: email.toLowerCase(),
        }),
        ...Object.entries(attributes).map(
          ([name, value]) => new CognitoUserAttribute({ Name: name, Value: value })
        ),
      ];

      userPool.signUp(
        email.toLowerCase(),
        password,
        userAttributes,
        [],
        err => {
          if (err) {
            console.error("Error during Cognito sign up:", err);
            reject(err.message || "Failed to sign up");
          }
          resolve();
        },
        metadata
      );
    } catch (err) {
      console.error("Error during Cognito sign up:", err);
      reject(err instanceof Error ? err.message : String(err));
    }
  });
};

/**
 * Confirms a user's sign-up in Cognito using a confirmation code.
 *
 * @param {Object} params - The parameters for confirming a user's sign-up.
 * @param {string} params.email - The email address of the user.
 * @param {string} params.code - The confirmation code sent to the user's email.
 * @returns {Promise<string>} A promise that resolves with a success message or rejects with an error message.
 */
export const confirmCognitoSignup = ({
  email,
  code,
}: {
  email: string;
  code: string;
}): Promise<string> => {
  const cognitoUser = getCognitoUser({ email });

  if (!cognitoUser) {
    return Promise.reject("User not found");
  }

  return new Promise((resolve, reject) => {
    cognitoUser.confirmRegistration(code, true, err => {
      if (err) {
        console.error("Failed to confirm sign-up", err);
        return reject(err.message || "Failed to confirm sign-up");
      }

      resolve("Sign-up confirmed successfully");
    });
  });
};

/**
 * Resends the confirmation code to the user's email.
 *
 * @param {CognitoResendConfirmationCodeParams} params - The parameters for resending the confirmation code.
 * @param {string} params.email - The email address of the user.
 * @param {Record<string, string>} [params.metadata] - Optional metadata to include in the resend request.
 * @returns {Promise<string>} A promise that resolves with a success message or rejects with an error message.
 */
export const cognitoResendConfirmationCode = ({
  email,
  metadata,
}: CognitoResendConfirmationCodeParams): Promise<string> => {
  const cognitoUser = getCognitoUser({ email });
  if (!cognitoUser) {
    return Promise.reject("User not found");
  }

  return new Promise((resolve, reject) => {
    cognitoUser.resendConfirmationCode(err => {
      if (err) {
        console.error("Failed to resend confirmation code", err);
        return reject(err.message || "Failed to resend confirmation code");
      }

      resolve("Confirmation code resent successfully");
    }, metadata);
  });
};

/**
 * Initiates the forgot password process for a user in Cognito.
 *
 * @param {string} email - The email address of the user requesting a password reset.
 * @returns {Promise<string>} A promise that resolves with a success message or rejects with an error message.
 */
export const forgotPassword = (email: string): Promise<string> => {
  const cognitoUser = getCognitoUser({ email });

  if (!cognitoUser) {
    return Promise.reject("User not found");
  }

  return new Promise((resolve, reject) => {
    cognitoUser.forgotPassword({
      onSuccess: () => {
        resolve("Password reset code sent successfully");
      },
      onFailure: err => {
        console.error("Failed to send password reset code", err);
        reject(err.message || "Failed to send password reset code");
      },
    });
  });
};

/**
 * Confirms a new password for a user in Cognito.
 *
 * @param {string} email - The email address of the user.
 * @param {string} verificationCode - The verification code sent to the user's email.
 * @param {string} newPassword - The new password to set for the user.
 * @returns {Promise<string>} A promise that resolves with a success message or rejects with an error message.
 */
export const confirmNewPassword = (
  email: string,
  verificationCode: string,
  newPassword: string
): Promise<string> => {
  const cognitoUser = getCognitoUser({ email });

  return new Promise((resolve, reject) => {
    cognitoUser.confirmPassword(verificationCode, newPassword, {
      onSuccess: () => {
        resolve("Password reset successfully");
      },
      onFailure: error => {
        reject(error.message || "Failed to reset password");
      },
    });
  });
};
